import {Button, makeStyles} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {useToast} from '@dishtech-react/hooks';
import axios from 'axios';
import PropTypes from 'prop-types';
import {validateRuidsCollection, separateUniqueRUIDsFromUserEntry, massDeleteBatchSize} from '../helpers/tools';
import ManageAssetsFromEntry from '../helpers/manage-assets-from-entry';
import {serviceConfig} from '../../../config/service-config';
import ProgressLinear from '../helpers/progress-linear';

const useStyles = makeStyles(theme => ({
  massDeleteRuidsWrapper: {
    position: 'relative',
    width: '552px',
  },
  mainButtonsContainer: {
    backgroundColor: 'white',
    bottom: '0px',
    display: 'flex',
    position: 'sticky',
    gap: theme.spacing(0.5),
    justifyContent: 'right',
    alignItems: 'center',
  },

  validRuidTableWrapper: {marginBottom: '15px'},
}));

const MassDeleteRuids = ({handleClose, onRuidMassDelete}) => {
  const {createToast} = useToast();
  const classes = useStyles();
  const [validatedRuidsMap, setValidatedRuidsMap] = useState(new Map());
  const [separatedRuidsFromEntry, setSeparatedRuidsFromEntry] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [selectedTableRows, setSelectedTableRows] = useState([]);
  const [deletingProgressValue, setDeletingProgressValue] = useState(0);
  const [numberOfDpsToDelete, setNumberOfDpsToDelete] = useState(0);
  const validDataPackagesObject = {
    ruidsFromEntry: separatedRuidsFromEntry,
    dataPackagesFromEntryMap: validatedRuidsMap,
  };

  useEffect(() => {
    setSelectedTableRows(Array(separatedRuidsFromEntry.length));
  }, [separatedRuidsFromEntry]);

  const validateFormEntry = RUIDsTextEntry => {
    const uniqueRUIDsFromEntry = separateUniqueRUIDsFromUserEntry(RUIDsTextEntry);
    validateRuidsCollection(uniqueRUIDsFromEntry).then(validMap => {
      setSeparatedRuidsFromEntry(uniqueRUIDsFromEntry);
      setValidatedRuidsMap(validMap);
    });
  };

  const handleClearEntry = () => {
    setValidatedRuidsMap(new Map());
    setSeparatedRuidsFromEntry([]);
  };

  const splitValidDPCollection = (dpCollection, elementsByArray = 3) => {
    let counter = 0;
    const collectorArray = [];
    while (counter < dpCollection.length) {
      collectorArray.push(dpCollection.slice(counter, counter + elementsByArray));
      counter += elementsByArray;
    }
    return collectorArray;
  };

  const deleteRuids = async ruidsToDelete => {
    if (ruidsToDelete.length < 1) {
      return createToast('Something went wrong: No valid data packages selected', {variant: 'error'});
    }

    setNumberOfDpsToDelete(ruidsToDelete.length);
    setDeleting(true);

    const {method, path, baseUrl} = serviceConfig.DELETE_DATA_PACKAGE;
    for (const subCollectionOfDpToDelete of splitValidDPCollection(ruidsToDelete, massDeleteBatchSize)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios[method.toLowerCase()](`${baseUrl}${path}`, {data: subCollectionOfDpToDelete});
        setDeletingProgressValue(prev => prev + massDeleteBatchSize);
      } catch (err) {
        setDeleting(false);
        return createToast(`Something went wrong: ${err.message}`, {variant: 'error'});
      }
    }
    setDeleting(false);
    setDeletingProgressValue(0);
    setNumberOfDpsToDelete(0);
    onRuidMassDelete();
    handleClose();
    createToast('Data packages were deleted successfully');
  };

  const handleDeleteAllValid = () => {
    const validRuids = separatedRuidsFromEntry.filter(ruid => validatedRuidsMap.has(ruid));
    deleteRuids(validRuids);
  };

  const handleDeleteSelected = () => {
    const selectedRuidsForDeletion = separatedRuidsFromEntry.filter((_, index) => selectedTableRows[index]);
    deleteRuids(selectedRuidsForDeletion);
  };

  return (
    <div className={classes.massDeleteRuidsWrapper}>
      <div className={classes.validRuidTableWrapper}>
        <ManageAssetsFromEntry
          dataPackagesFromEntryMap={validatedRuidsMap}
          handleClearEntry={handleClearEntry}
          ruidsFromEntry={separatedRuidsFromEntry}
          selectedTableRows={selectedTableRows}
          setSelectedTableRows={setSelectedTableRows}
          validDataPackagesObject={validDataPackagesObject}
          onRUIDEntryFormSubmit={validateFormEntry}
        />
      </div>

      <div className={classes.mainButtonsContainer}>
        <Button variant="contained" onClick={handleClearEntry}>
          Clear
        </Button>
        <Button color="primary" variant="contained" onClick={handleDeleteSelected}>
          Delete Selection
        </Button>
        <Button color="primary" variant="contained" onClick={handleDeleteAllValid}>
          Delete All Valid
        </Button>
        <Button color="secondary" variant="contained" onClick={handleClose}>
          Cancel
        </Button>
      </div>
      {deleting ? (
        <ProgressLinear
          progressTitle={`${deletingProgressValue} of ${numberOfDpsToDelete} data packages deleted`}
          progressValue={(deletingProgressValue / numberOfDpsToDelete) * 100}
          replaceAllText={true}
          size={20}
          title="Deleting Data Packages"
        />
      ) : null}
    </div>
  );
};

MassDeleteRuids.propTypes = {
  handleClose: PropTypes.func,
  onRuidMassDelete: PropTypes.func,
};

export default MassDeleteRuids;
